/**
 * @class
 * @description This class maintains the model structure of our environment.json file.
 * @exports {EnvironmentModel}
 * @name EnvironmentModel
 */
export class EnvironmentModel {

  /**
   * @description This property is the API URL of our environment.json file.
   * @memberof EnvironmentModel
   * @name EnvironmentModel#apiUrl
   * @property
   * @public
   * @type {string}
   */
  public apiUrl: string;

  /**
   * @description This property is the development flag of our environment.json file.
   * @memberof EnvironmentModel
   * @name EnvironmentModel#development
   * @property
   * @public
   * @type {boolean}
   */
  public development: boolean;

  /**
   * @description This property is the local flag of our environment.json file.
   * @memberof EnvironmentModel
   * @name EnvironmentModel#local
   * @property
   * @public
   * @type {boolean}
   */
  public local: boolean;

  /**
   * @description This property is the production flag of our environment.json file.
   * @memberof EnvironmentModel
   * @name EnvironmentModel#production
   * @property
   * @public
   * @type {boolean}
   */
  public production: boolean;

  /**
   * @description Constructs the environment model with its default values.
   * @memberof EnvironmentModel
   * @method
   * @name EnvironmentModel#constructor
   * @public
   */
  constructor() {
    this.apiUrl = 'http://localhost:5000';
    this.development = true;
    this.local = true;
    this.production = false;
  }

  /**
   * @description This method creates an environment model from an object.
   * @memberof EnvironmentModel
   * @method
   * @name EnvironmentModel.fromObject
   * @param {{ [key: string]: any }} environmentObject The object from which to create the environment model.
   * @public
   * @returns {EnvironmentModel} The environment model created from the object.
   * @static
   */
  public static fromObject(environmentObject: { [key: string]: any }): EnvironmentModel {

    // Instantiate our response model.
    const response: EnvironmentModel = new EnvironmentModel();

    // Iterate over each public property of the response.
    Object.keys(response).forEach((property: string): void => {

      // If the property exists in the object, populate the response.
      if (!response.hasOwnProperty(property)) delete environmentObject[property];
    });

    // We're done, send the populated response.
    return Object.assign(response, environmentObject);
  }

  /**
   * @description This method normalizes the API URL of the environment model.
   * @memberof EnvironmentModel
   * @method
   * @name EnvironmentModel#normalizedApiUrl
   * @param {string} [path] The optional path to append to the normalized API URL.
   * @param {{ [key: string]: any }} [parameters] The optional parameters to append to the normalized API URL.
   * @public
   * @returns {string} The normalized API URL.
   */
  public normalizedApiUrl(path?: string, parameters?: { [key: string]: any }): string {

    // Ensure we have parameters.
    parameters ??= {};

    // Normalize the optional path.
    let normalizedPath: string = path?.trim()?.startsWith('/')
      ? path.trim().substring(1)
      : path?.trim() ?? '';

    // Iterate over the provided parameters or an empty array if none were provided.
    for (const key of Object.keys(parameters ?? {}))
      normalizedPath = normalizedPath.replace(new RegExp(`:${key.trim()}`, 'gi'),
        encodeURIComponent(parameters[key.trim()]?.toString().trim()));

    // We're done, return the normalized API URL.
    return normalizedPath.trim().startsWith('/') ? normalizedPath.trim() : `/${normalizedPath.trim()}`;
  }

  /**
   * @description This method normalizes the full API URL of the environment model.
   * @memberof EnvironmentModel
   * @method
   * @name EnvironmentModel#normalizedFullApiUrl
   * @param {string} [path] The optional path to append to the normalized API URL.
   * @param {{ [key: string]: any }} [parameters] The optional parameters to append to the normalized API URL.
   * @public
   * @returns {string} The normalized API URL.
   */
  public normalizedFullApiUrl(path?: string, parameters?: { [key: string]: any }): string {

    // We're done, return the normalized API URL.
    return this.apiUrl + this.normalizedApiUrl(path, parameters);
  }
}
